import type { BackfillComputed } from '../types';
import { getPriorityLabel } from '../utils/task-types';
import { isEmptyString } from '@/utils/string';
import { Box, FormattedRelativeTime, Stack, Strong, Text, Tooltip } from '@customerio/pluma-components/react';
import { formatDateTime } from '@customerio/pluma-components/react/utils/formatted-date-time';
import type { Row } from '@tanstack/table-core';
import { formatDistance } from 'date-fns';
import * as styles from './task-expanded-row.css';

interface TaskExpandedRowProps {
  row: Row<BackfillComputed>;
}

export function TaskExpandedRow({ row }: TaskExpandedRowProps) {
  const backfill = row.original;

  return (
    <Box display="flex" w="full" gap="100" flexDirection="column" className={styles.expandedRowContent}>
      <Stack gap="100">
        {backfill.isPending === true || isEmptyString(backfill.status) === false ? (
          <Text size="s">{backfill.status ?? backfill.info.status}</Text>
        ) : null}
        {['Canceled', 'Failure'].includes(backfill.state) && !isEmptyString(backfill.error) ? (
          <Text size="s">
            <Strong>Error:</Strong> {backfill.error}
          </Text>
        ) : null}
        <Text size="s">
          <Strong>{getPriorityLabel(backfill.priority)}</Strong> priority task
        </Text>
      </Stack>

      <Text size="s" color="subtle">
        Created <FormattedRelativeTime date={backfill.created} color="base" withTooltip={true} />
      </Text>
      {backfill.started != null && backfill.started !== 0 ? (
        <Text size="s" color="subtle">
          Queued for{' '}
          <Tooltip content={`Left queue on ${formatDateTime(backfill.started)}`} cursor="help">
            <Text color="base">{formatDistance(backfill.created * 1000, backfill.started * 1000)}</Text>
          </Tooltip>
        </Text>
      ) : null}
      {backfill.completed != null && backfill.completed !== 0 && backfill.started != null && backfill.started !== 0 ? (
        <Text size="s" color="subtle">
          Ran for{' '}
          <Tooltip content={`Finished running on ${formatDateTime(backfill.completed)}`} cursor="help">
            <Text color="base">{formatDistance(backfill.started * 1000, backfill.completed * 1000)}</Text>
          </Tooltip>
        </Text>
      ) : null}
    </Box>
  );
}
